import React, { useState, useEffect } from "react";
import { Button, Form, Row, Col, Pagination, Alert, Spinner } from "react-bootstrap";
import { TbFileTime } from "react-icons/tb";
import PayrollLayout from "../../components/PayrollLayout.jsx";

const API_URL = "https://backend.ems.uk.hiradentallab.com/api/payroll";

const PayrollReport = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [counter, setCounter] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [payrollData, setPayrollData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [includeUnapprovedOvertime, setIncludeUnapprovedOvertime] = useState(false);
  const itemsPerPage = 10;

  /* ---------- Working days counter ---------- */
  const calculateWorkingDays = (start, end) => {
    if (!start || !end) return 0;
    let startDateObj = new Date(start);
    let endDateObj = new Date(end);

    if (startDateObj > endDateObj) {
      [startDateObj, endDateObj] = [endDateObj, startDateObj];
    }

    let count = 0;
    const currentDate = new Date(startDateObj);
    while (currentDate <= endDateObj) {
      if (currentDate.getDay() !== 0) count++; // exclude Sundays
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return count;
  };

  useEffect(() => {
    if (startDate && endDate) {
      try {
        setCounter(calculateWorkingDays(startDate, endDate));
      } catch (err) {
        console.error("Error calculating working days:", err);
        setCounter(0);
      }
    } else {
      setCounter(0);
    }
  }, [startDate, endDate]);

  const handleChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setCounter(isNaN(value) ? 0 : value);
  };

  const validateForm = () => startDate && endDate && counter > 0;

  /* ---------- Generate payroll via API ---------- */
  const handleGenerateReport = async () => {
    setShowErrors(true);
    if (!validateForm()) return;

    // Derive month/year from end date (payroll period ends on the 25th)
    const end = new Date(endDate);
    const month = end.getMonth() + 1;
    const year = end.getFullYear();

    setLoading(true);
    setAlert(null);

    try {
      const res = await fetch(`${API_URL}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month, year, includeUnapprovedOvertime }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to generate payroll");
      }

      setPayrollData(data.data || []);
      setShowTable(true);
      setCurrentPage(1);

      setAlert({
        variant: data.errors && data.errors.length > 0 ? "warning" : "success",
        message: data.message,
        errors: data.errors,
      });
    } catch (err) {
      setAlert({ variant: "danger", message: err.message });
      setShowTable(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleGenerateReport();
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const totalPages = Math.ceil(payrollData.length / itemsPerPage);

  const pageRange = () => {
    const range = [];
    for (let i = 1; i <= totalPages; i++) range.push(i);
    return range;
  };

  const currentData = payrollData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleExportToExcel = () => console.log("Exporting to Excel");
  const handleExportToPDF = () => console.log("Exporting to PDF");

  const calculateTotal = (field) =>
    currentData.reduce((sum, item) => sum + (Number(item[field]) || 0), 0);

  const fmt = (v, d = 2) => (Number(v) || 0).toFixed(d);

  return (
    <PayrollLayout>
      <h1 className="all-report-title">
        <TbFileTime /> Generate Payroll
      </h1>
      <hr />

      <Form className="all-report-form" onSubmit={handleSubmit}>
        <Row>
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label htmlFor="start_date">
                Start date <span className="required">*</span> :
              </Form.Label>
              <Form.Control
                type="date"
                id="start_date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                isInvalid={showErrors && !startDate}
              />
              <Form.Control.Feedback type="invalid">
                {showErrors && !startDate ? "Start date is required" : ""}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label htmlFor="end_date">
                End date <span className="required">*</span> :
              </Form.Label>
              <Form.Control
                type="date"
                id="end_date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                isInvalid={showErrors && !endDate}
              />
              <Form.Control.Feedback type="invalid">
                {showErrors && !endDate ? "End date is required" : ""}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label htmlFor="working-days">
                Working Days<span className="required">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                id="working-days"
                value={counter}
                onChange={handleChange}
                min="1"
                step="1"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col xs={12}>
            <Form.Check
              type="checkbox"
              id="includeUnapprovedOvertime"
              label="Include unapproved overtime in payable hours"
              checked={includeUnapprovedOvertime}
              onChange={(e) => setIncludeUnapprovedOvertime(e.target.checked)}
            />
          </Col>
        </Row>

        <Button
          className="generate-report-button mt-3"
          type="submit"
          disabled={!validateForm() || loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            "Generate Report"
          )}
        </Button>
      </Form>

      {alert && (
        <Alert
          variant={alert.variant}
          className="mt-3"
          dismissible
          onClose={() => setAlert(null)}
        >
          {alert.message}
          {alert.errors && alert.errors.length > 0 && (
            <ul className="mb-0 mt-2">
              {alert.errors.map((e, i) => (
                <li key={i}>
                  <strong>{e.userName}</strong> ({e.userId}): {e.error}
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}

      {showTable && (
        <div className="emps-leave-report-table-container">
          <div className="table-actions">
            <Row className="w-100">
              <Col
                xs={12}
                md={6}
                className="d-flex align-items-center mb-3 justify-content-start"
              >
                <Button
                  variant="secondary"
                  className="export-button"
                  onClick={handleExportToExcel}
                >
                  Excel
                </Button>
                <Button
                  variant="secondary"
                  className="export-button"
                  onClick={handleExportToPDF}
                  style={{ marginLeft: "10px" }}
                >
                  PDF
                </Button>
              </Col>
            </Row>
          </div>

          <div className="table-responsive">
            <table className="table table-striped">
              <thead className="table-header">
                <tr className="emps-payroll-table-header">
                  <th>SR</th>
                  <th>User ID</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Monthly Salary</th>
                  <th>Contracted Hrs</th>
                  <th>Hourly Rate</th>
                  <th>Worked Hours</th>
                  <th>Present</th>
                  <th>Absent</th>
                  <th>Leave</th>
                  <th>OT Hours</th>
                  <th>Gross Salary</th>
                  <th>Net Salary</th>
                  <th>Period</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((data, index) => (
                  <tr key={data._id || index}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{data.userId}</td>
                    <td>{data.userName}</td>
                    <td>{data.department}</td>
                    <td>£{fmt(data.monthlySalary)}</td>
                    <td>{fmt(data.contractedHours)}</td>
                    <td>£{fmt(data.hourlyRate, 4)}</td>
                    <td>{fmt(data.totalWorkedHours)}</td>
                    <td>{data.presentDays}</td>
                    <td>{data.absentDays}</td>
                    <td>{data.leaveDays}</td>
                    <td>{fmt(data.totalOvertimeHours)}</td>
                    <td>£{fmt(data.grossSalary)}</td>
                    <td>£{fmt(data.netSalary)}</td>
                    <td>{data.periodLabel}</td>
                    <td>
                      <span className={`badge ${data.status === "paid" ? "bg-success" : data.status === "finalized" ? "bg-info" : "bg-secondary"}`}>
                        {data.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-footer">
                <tr className="emps-payroll-table-footer">
                  <td>Total:</td>
                  <td>{currentData.length} employees</td>
                  <td></td>
                  <td></td>
                  <td>£{fmt(calculateTotal("monthlySalary"))}</td>
                  <td>{fmt(calculateTotal("contractedHours"))}</td>
                  <td></td> {/* Hourly Rate - no total */}
                  <td>{fmt(calculateTotal("totalWorkedHours"))}</td>
                  <td>{calculateTotal("presentDays")}</td>
                  <td>{calculateTotal("absentDays")}</td>
                  <td>{calculateTotal("leaveDays")}</td>
                  <td>{fmt(calculateTotal("totalOvertimeHours"))}</td>
                  <td>£{fmt(calculateTotal("grossSalary"))}</td>
                  <td>£{fmt(calculateTotal("netSalary"))}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>

            <div className="pagination-container">
              <Row>
                <Col xs={12} md={6} className="d-flex align-items-center">
                  <span>
                    Showing {currentPage} of {totalPages || 1} Pages
                  </span>
                </Col>
                <Col xs={12} md={6} className="d-flex justify-content-end">
                  <Pagination>
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Pagination.Prev>
                    {pageRange().map((page) => (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Pagination.Next>
                  </Pagination>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}
    </PayrollLayout>
  );
};

export default PayrollReport;